import { ReactNode, useEffect } from "react"
import { useSelector } from "react-redux"
import {
  haLoginSession,
  initializeSession,
  selectAccountGuid,
  selectMsalInitialized,
  selectAuthenticationStatus,
  signUpSession,
  AuthStatus,
} from "../authenticationSlice"
import { selectValidatedHealthAdvocateUser } from "../../shared/selectors"
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from "../../../reduxStore/hooks"
import { useLocation } from "react-router"
import { useSearchParams } from "react-router-dom"
import { getLogger } from "loglevel"
import {
  getPracticeRoles,
  selectAllPracticeRoles,
  selectCurrentUserRoles,
  selectUserDataStatus,
} from "../../../domain/Account/userSlice"
import HALogo from "./../../../assets/HALogo.svg"
import splashBackground from "./../../../assets/splashBackground.png"
import footerCurtain from "./../../../assets/footerCurtain.svg"
import greyHeartFooter from "./../../../assets/greyHeartFooter.svg"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import { appRoutes, publicRoutes } from "../../../routes"
import Loading from "../../../pages/Loading"
import { Typography, typographyTypes } from "../../shared/components/Typography"
import Label from "../../shared/components/Label"
import ColumnContainer from "../../shared/components/Layout/ColumnContainer"
import Button from "../../shared/components/Button"
import RowContainer from "../../shared/components/Layout/RowContainer"
import { ButtonWrapper } from "../../shared/components/ButtonWrapper"
import Icon from "../../shared/components/Icon"

interface AuthProps {
  children: ReactNode
}

export const WithAuthentication = (props: AuthProps) => {
  const { cms: cmsContent } = useCMSReady(["withAuthentication"])

  // Hooks
  const authStatus = useAppSelector(selectAuthenticationStatus)
  const msalInitialized = useAppSelector(selectMsalInitialized)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useAppNavigate()

  const [searchParams, _setSearchParams] = useSearchParams()
  const log = getLogger("WithAuthentication")
  const userId = useSelector(selectAccountGuid)
  const userRoles = useSelector(selectCurrentUserRoles)
  const userPracticeRoles = useSelector(selectAllPracticeRoles)
  const isHaUser = useAppSelector(selectValidatedHealthAdvocateUser)
  const userDataStatus = useSelector(selectUserDataStatus)

  const withAuthenticationText = cmsContent.withAuthentication

  useEffect(() => {
    if (!msalInitialized) {
      dispatch(initializeSession())
    }

    if (
      authStatus === AuthStatus.Authenticated &&
      !isHaUser &&
      userRoles.length > 0 &&
      !userRoles.includes("role-affiliate")
    ) {
      console.warn(
        "User is an NOT a role-affiliate user, and not an HA user. This is unexpected.",
      )
    }

    if (
      userId &&
      userDataStatus.practiceRolesDataStatus === "loading" &&
      userPracticeRoles.length === 0 &&
      !isHaUser
    ) {
      dispatch(getPracticeRoles({ userId }))
    }
  }, [
    userId,
    userRoles,
    userPracticeRoles,
    isHaUser,
    msalInitialized,
    dispatch,
    authStatus,
    userDataStatus.practiceRolesDataStatus,
  ])

  // State
  const isLoading =
    authStatus === AuthStatus.Authenticating ||
    authStatus === AuthStatus.Unauthenticating

  const isAuthenticated = authStatus === AuthStatus.Authenticated

  // Check for HA login / MemPHIS handoff
  const caseId = searchParams.get("caseId")
  useEffect(() => {
    if (location.pathname === appRoutes.HA_LOGIN_ROUTE && caseId) {
      // auto-redirect for HA handoff
      log.info("Redirecting for HA handoff with caseId: " + caseId)
      dispatch(haLoginSession(caseId))
    }
  }, [location.pathname, caseId, authStatus, dispatch, navigate, log])

  const mainContent = <>{props.children}</>

  // Note: HA Design system styles
  const cardBoldTextStyle =
    "text-center text-[#273137] font-brandon-grotesque !text-[24px] !leading-[20px] !font-[450]"
  const cardTextStyle =
    "text-center text-[#273137] text-xl font-brandon-grotesque !text-[24px] !leading-[24px] !font-[390] px-3"

  const cardBackgroundStyle =
    "w-[254px] h-[255px] bg-white !bg-opacity-60 rounded-tr-[40px] rounded-bl-[41px] content-center text-center mb-5"

  // Render
  if (isLoading) {
    return <Loading />
  } else if (publicRoutes.includes(location.pathname) || isAuthenticated) {
    return mainContent
  } else {
    const newProviderButtonLabel = (
      <Typography
        variant={typographyTypes.NORMAL}
        text={withAuthenticationText!.createAccount}
        // Note: HA Design system styles
        styleOverrides="px-4 text-white !text-[17px] !font-[500] leading-normal font-brandon-grotesque"
      />
    )
    return (
      <div className="flex flex-col box-border">
        <div className="relative">
          <div className="h-full">
            {/* Simulated Top Gradient Border */}
            <div className="relative">
              <div className="h-[7px] bg-gradient-to-r from-[#660F8E] to-[#D3913E] absolute top-0 left-0 right-0 z-10"></div>
            </div>

            {/* Content of the page */}
            <div
              className="relative p-4 box-border bg-cover bg-no-repeat w-full h-full min-h-[720px]"
              style={{
                backgroundImage: `url(${splashBackground})`,
                backgroundPosition: "top",
              }}
            >
              <div className="flex flex-col space-y-2 content-center justify-center mt-10">
                <div className="text-center">
                  <Label
                    htmlFor={withAuthenticationText!.welcomeTitle1}
                    text={`${withAuthenticationText!.welcomeTitle1} ${withAuthenticationText!.welcomeTitle2}`}
                    wrap
                    textCentered
                    // Note: HA Design system style
                    styleOverrides="text-[#2d383f] text-[73.73px] leading-[86.01px] font-[450] tracking-[-1.843px] font-brandon-grotesque"
                  />
                </div>
                <ColumnContainer styleOverrides="text-center align-center justify-center">
                  <Label
                    htmlFor={withAuthenticationText!.welcomeSubTitle}
                    text={withAuthenticationText!.welcomeSubTitle}
                    wrap
                    textCentered
                    // Note: HA Design system style
                    styleOverrides="text-center text-[#2d383f] text-[32px] font-[390] leading-[30px] font-brandon-grotesque"
                  />
                  <div className="flex flex-col items-center !mt-11">
                    <Button
                      buttonLabel={newProviderButtonLabel}
                      ariaLabel={withAuthenticationText!.createAccount}
                      type="purpleBrand"
                      textBold
                      buttonSize="xl"
                      onClick={() => {
                        dispatch(signUpSession())
                      }}
                    />
                    <div className="bg-white/60 px-[12px] mt-[27px] max-w-[405px]">
                      <Typography
                        variant={typographyTypes.NORMAL}
                        text={withAuthenticationText?.createAccountDescription}
                        styleOverrides="py-[6px] text-[#1C1917] text-center font-inter text-xs font-medium"
                      />
                    </div>
                  </div>
                </ColumnContainer>
              </div>

              <RowContainer styleOverrides="mt-[118px] justify-around flex-wrap">
                {/* Map Pins */}
                <div className={cardBackgroundStyle}>
                  <div>
                    <Icon iconType="mapPins" />
                  </div>
                  <div className={cardBoldTextStyle}>
                    {withAuthenticationText!.mapPinsTitle}
                  </div>
                  <div className={cardTextStyle}>
                    {withAuthenticationText!.mapPinsSubTitle}
                  </div>
                </div>

                {/* Contact Icon */}
                <div className={cardBackgroundStyle}>
                  <div>
                    <Icon
                      iconType="profileCard"
                      styleOverrides="w-[95.8px] h-[76px]"
                    />
                  </div>
                  <div className={cardBoldTextStyle}>
                    {withAuthenticationText!.cardProfileTitle}
                  </div>
                  <div className={cardTextStyle}>
                    {withAuthenticationText!.cardProfileSubTitle}
                  </div>
                </div>

                {/* Money Icon */}
                <div className={cardBackgroundStyle}>
                  <div>
                    <Icon iconType="fastMoney" />
                  </div>
                  <div className={cardBoldTextStyle}>
                    {withAuthenticationText!.moneyCardTitle}
                  </div>
                  <div className={cardTextStyle}>
                    {withAuthenticationText!.moneyCardSubTitle}
                  </div>
                </div>

                {/* FAQ */}
                <div
                  className={`${cardBackgroundStyle} cursor-pointer`}
                  onClick={() => {
                    window.open(
                      "https://www.healthadvocate.com/site/careconnect",
                      "_blank",
                    )
                  }}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      window.open(
                        "https://www.healthadvocate.com/site/careconnect",
                        "_blank",
                      )
                    }
                  }}
                >
                  <div>
                    <Icon iconType="questionPurple" />
                  </div>
                  <div className={cardBoldTextStyle}>
                    {withAuthenticationText!.faqTitle}
                  </div>
                  <div className={cardTextStyle}>
                    {withAuthenticationText!.faqSubTitle}
                  </div>
                </div>
              </RowContainer>
            </div>
          </div>
        </div>

        {/* Raspberry text ribbon */}
        {/* Note: HA Design system styles */}
        <div className="flex flex-col w-full justify-center items-center py-[35px] space-y-10 bg-raspberry text-white font-[450] text-[24px] leading-[20px] font-brandon-grotesque">
          {withAuthenticationText!.footerTitle}
        </div>

        {/* Footer */}
        <div
          // This div is styled inline to force safari to render correctly. Doing it via tailwind breaks safari.
          style={{
            bottom: "0",
            left: "0",
            width: "100%",
            height: "500px",
            backgroundImage: `url(${footerCurtain}), url(${greyHeartFooter})`,
            backgroundPosition: "bottom, bottom",
            backgroundSize: "100%, 100%",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#EBEBEB", // tertiary-grey
          }}
        >
          {/* HA Copyright */}
          <div className="flex flex-col w-full space-y-5 items-center mb-5">
            <img
              className="w-60 md:w-80 mt-24 mb-6"
              src={HALogo}
              alt="Health Advocate"
            />
            <div className="md:w-1/3">
              <Label
                htmlFor={withAuthenticationText!.haBlurb}
                text={withAuthenticationText!.haBlurb}
                size="small"
                color="black"
                textCentered
                wrap
              />
            </div>
            <Label
              htmlFor={withAuthenticationText!.copyright}
              text={withAuthenticationText!.copyright}
              size="small"
              color="black"
              textCentered
            />
          </div>

          {/* Social */}
          <div className="flex flex-row w-full mt-0 justify-center items-center space-x-5 bg-transparent">
            <ButtonWrapper
              key="facebook"
              styleOverrides="bg-tertiary-grey"
              width="fit"
              onClick={() =>
                window.open(
                  "https://www.facebook.com/healthadvocateinc",
                  "_blank",
                )
              }
            >
              <Icon iconType="facebook" />
            </ButtonWrapper>
            <ButtonWrapper
              key="twitter"
              styleOverrides="bg-tertiary-grey"
              width="fit"
              onClick={() =>
                window.open("https://twitter.com/healthadvocate", "_blank")
              }
            >
              <Icon iconType="twitter" />
            </ButtonWrapper>
            <ButtonWrapper
              key="linkedin"
              styleOverrides="bg-tertiary-grey"
              width="fit"
              onClick={() =>
                window.open(
                  "https://www.linkedin.com/company/health-advocate",
                  "_blank",
                )
              }
            >
              <Icon iconType="linkedin" />
            </ButtonWrapper>
          </div>
        </div>
      </div>
    )
  }
}

export default WithAuthentication
